import { LANGUAGE_STORAGE_KEY } from '../i18n/languages';

/**
 * Device-local keys cleared by “Clear cached app data”.
 * Everything else under `ofrontend.*` is user data (favourites, booking profile,
 * asset-create drafts, profile hints, login convenience, etc.) and is kept.
 */
const EPHEMERAL_EXACT_KEYS = new Set([
  'ofrontend.sessionToken',
  'ofrontend.refreshToken',
  'ofrontend.userName',
  'ofrontend.loginType',
  'ofrontend.loginIdentifier',
  'ofrontend.userLogoutGate',
  'ofrontend.debugFullAssetApi',
  /** Rebuildable home browse pool (see homeAssetSearch). */
  'ofrontend.homeAssetPool.v1',
  /** Messaging peer display cache. */
  'ofrontend.messagingPeerCache.v1',
  /** Transient refresh gates (sessionStorage). */
  'ofrontend.refreshUnavailable404',
]);

/** Legacy UI-only keys (profile tab history, etc.). */
const EPHEMERAL_PREFIXES = ['ofrontendProfile'];

export function isEphemeralAppLocalStorageKey(key: string): boolean {
  const trimmed = key.trim();
  if (!trimmed) {
    return false;
  }
  if (EPHEMERAL_EXACT_KEYS.has(trimmed)) {
    return true;
  }
  return EPHEMERAL_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
}

export type AppLocalStoragePreservationOptions = {
  /** Keep Hebrew/English preference (default true). */
  preserveLanguage?: boolean;
  /** Keep favourites, drafts, profile hints, booking form data, etc. (default true). */
  preserveUserData?: boolean;
};

export function shouldPreserveAppLocalKey(
  key: string,
  options?: AppLocalStoragePreservationOptions,
): boolean {
  if (key === LANGUAGE_STORAGE_KEY) {
    return options?.preserveLanguage !== false;
  }
  if (options?.preserveUserData === false) {
    return false;
  }
  return !isEphemeralAppLocalStorageKey(key);
}
